import React, { useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button, Grid, Typography, Box } from '@mui/material';
import { useNotification } from '../../../components/notification/NotificationContext';

import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { formikValidation } from '@/common/models/formikValidation';
import { FormTextField } from '@/common/components/form/FormTextField';
import { LoadingSpinner } from '@/common/components/loading/LoadingSpinner';
import {
  useGetUserProfileMinimalQuery,
  useGetUserProfileRegisterQuery,
  UserType,
  UserUpdateInput,
  useUpdateUserProfileMutation,
} from '@/generated/graphql';

import { useAuth } from '@/common/auth/hooks/useAuth';

const RegisterForm: React.FC = () => {
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [updateUser, { loading: profileMutationLoading }] =
    useUpdateUserProfileMutation();

  const { refetch: refetchProfileMinimal, loading: profileMinimalLoading } =
    useGetUserProfileMinimalQuery({
      fetchPolicy: 'cache-first',
      skip: true,
    });

  const {
    loading: profileLoading,
    error: profileError,
    data: profileData,
    refetch: refetchProfile,
  } = useGetUserProfileRegisterQuery({
    fetchPolicy: 'network-only', // Always fetch fresh data
    errorPolicy: 'all', // Return partial data even if there are errors
    skip: !isAuthenticated,
  });

  // Log error for debugging - must be before conditional returns
  useEffect(() => {
    if (profileError) {
      console.error('RegisterForm profile error:', profileError);
      // Try to refetch once
      setTimeout(() => {
        refetchProfile().catch((err) => {
          console.error('Profile refetch failed:', err);
        });
      }, 1000);
    }
  }, [profileError, refetchProfile]);

  const user = React.useMemo(() => {
    return profileData?.user as UserType;
  }, [profileData]);
  const canSkipForm = React.useMemo(() => {
    return !profileLoading && !profileError && user?.artistName;
  }, [profileLoading, profileError, user]);

  if (profileLoading) {
    return <LoadingSpinner message="Loading account..." />;
  }

  if (profileError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
          gap: 2,
        }}
      >
        <Typography>Error loading account</Typography>
        <Typography variant="body2" color="text.secondary">
          {profileError.message || 'Please try refreshing the page'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Formik
        enableReinitialize
        validateOnBlur
        initialValues={
          {
            artistName: user?.artistName || '',
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
          } as UserUpdateInput
        }
        validationSchema={Yup.object().shape({
          ...formikValidation.firstName,
          ...formikValidation.lastName,
          ...formikValidation.artistName,
        })}
        onSubmit={async (value) => {
          try {
            const result = await updateUser({
              variables: {
                data: {
                  firstName: value?.firstName,
                  lastName: value?.lastName,
                  artistName: value?.artistName,
                },
              },
            });

            if (result.data?.userUpdate._id) {
              showSuccess('Profile saved successfully');
              refetchProfile();
              await refetchProfileMinimal();

              // Redirect to
              navigate('/projects');
            }
          } catch (error) {
            console.error(error);
            showError('Failed to update profile. Please try again.');
          }
        }}
      >
        {({ handleSubmit, isSubmitting, dirty, isValid, initialTouched }) => (
          <Form onSubmit={handleSubmit}>
            <Grid container spacing={2} direction="column" mt={1}>
              <Grid size={{ xs: 12, sm: 12 }}>
                <FormTextField
                  required
                  name="firstName"
                  label="First Name"
                  type="firstName"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12 }}>
                <FormTextField
                  required
                  name="lastName"
                  label="Last Name"
                  type="lastName"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12 }}>
                <FormTextField
                  required
                  name="artistName"
                  label="Artist Name"
                  type="artistName"
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                pt: 3,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ maxWidth: 270, lineHeight: 1.5 }}
              >
                By continuing, you agree to the{' '}
                <RouterLink to="/terms" target="_blank" rel="noreferrer">
                  Terms
                </RouterLink>{' '}
                and acknowledge the{' '}
                <RouterLink to="/privacy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </RouterLink>
                .
              </Typography>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                name="updateProfile"
                title="Update Profile"
                disabled={
                  !(isValid && dirty && initialTouched && !isSubmitting) &&
                  !canSkipForm
                }
                loading={profileMutationLoading || profileMinimalLoading}
              >
                Continue
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default RegisterForm;
